import { formattedCurrencyToNumber } from "utils/formatter-utils/currency-formatter";
import { Dispatch, SetStateAction } from "react";
import {getRequest, postRequest} from "utils/httpClient";
import { Bet } from "interfaces/bet";
import {getConfirmBetPath, integratedCreateBet, integratedGetBetDetails,} from "utils/backend-path-builders";
import { reportError } from "utils/report-error";

const POLLING_FREQUENCY = 200;
const WAIT_TIME_BEFORE_POLLING = 200;
const MAX_RETRIES = 30;

const pollForBetDetails = (
    betId: string,
    onSuccessfulBetPlaced: any,
    setLoading: any,
    setErrorMessage: any,
    retryNumber: number,
) =>{
    // This poller already owns its own retry loop and treats a 5xx as an
    // expected "bet not written yet" signal, so opt out of the http client's
    // built-in transient-retry to keep timing under this loop's control.
    getRequest(integratedGetBetDetails(betId), { reportErrorToSentry: false, skipRetry: true })
        .then((data) => {
            if (data.error || data.technical_error) {
                if (retryNumber >= MAX_RETRIES) {
                    reportError(
                        new Error(`bet confirmation never resolved after ${MAX_RETRIES} polls`),
                        "bet_confirmation_polling",
                        { betId, lastResponse: data }
                    );
                    setErrorMessage(data.friendly_error_message || "Failed to fetch bet details");
                    setLoading(false);
                } else {
                    setTimeout(
                        () =>
                            pollForBetDetails(
                                betId,
                                onSuccessfulBetPlaced,
                                setLoading,
                                setErrorMessage,
                                retryNumber + 1,
                            ),
                        POLLING_FREQUENCY
                    );
                }
            } else {
                onSuccessfulBetPlaced(data);
            }
        })
        .catch((e) => {
            if (retryNumber >= MAX_RETRIES) {
                reportError(e, "bet_confirmation_polling", { betId });
                const responseBody = e.response?.data;
                const technicalMessage = responseBody?.technical_message;
                let errorMessage
                if (responseBody?.code === 10005 && technicalMessage !== "") {
                  errorMessage = technicalMessage;
                } else {
                  errorMessage = e?.response?.data?.friendly_error_message || "Failed to fetch bet details";
                }
                setErrorMessage(errorMessage);
                setLoading(false);
            } else {
                setTimeout(
                    () =>
                        pollForBetDetails(
                            betId,
                            onSuccessfulBetPlaced,
                            setLoading,
                            setErrorMessage,
                            retryNumber + 1,
                        ),
                    POLLING_FREQUENCY
                );
            }
        });
}


interface ConfirmBetArguments {
  isIntegratedApp: boolean;
  betAmount: string;
  pool_id: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setErrorMessage: Dispatch<SetStateAction<string | undefined>>;
  poolEntryId: string;
  groupId: string;
  onSuccessfulBetPlaced: (bet: Bet) => void;
  currency: string;
  contest_entry_id?: string;
}
const confirmBet = ({
  isIntegratedApp,
  betAmount,
  poolEntryId,
  pool_id,
  setLoading,
  setErrorMessage,
  onSuccessfulBetPlaced,
  groupId,
  currency,
  contest_entry_id,
}: ConfirmBetArguments) => {
  setLoading(true);
  const bet_amount = formattedCurrencyToNumber(betAmount);
  setErrorMessage("");
  const path = isIntegratedApp
    ? integratedCreateBet(pool_id, poolEntryId)
    : getConfirmBetPath(groupId, pool_id, poolEntryId);
  postRequest(path, {
    body: {
      bet_amount,
      currency,
      contest_entry_id,
    },
  })
    .then((data) => {
      if (data.code === 10005) {
        if (data.technical_message !== "") {
          setErrorMessage(data.technical_message);
        } else {
          setErrorMessage(
              "Cannot create transaction. Please check your balance or try again later"
          );
        }
        setLoading(false);
      } else if (data.bet_id && data.transaction_id) {
          setTimeout(
              () =>
                  pollForBetDetails(
                      data.bet_id,
                      onSuccessfulBetPlaced,
                      setLoading,
                      setErrorMessage,
                      0,
                  ),
              WAIT_TIME_BEFORE_POLLING
          );
      } else if (data.id === undefined) {
        throw new Error("Something went wrong.");
      }  else {
        onSuccessfulBetPlaced(data);
      }
    })
    .catch((error) => {
        reportError(error, "bet_placement", { pool_id, poolEntryId, contest_entry_id });
        setLoading(false);

        const responseBody = error.response?.data;
        const technicalMessage = responseBody?.technical_message;

        let message;
        if (responseBody?.code === 10005 && technicalMessage !== "") {
          message = technicalMessage;
        } else {
          const fallback = "Cannot create transaction. Please check your balance or try again later";
          message = error?.response?.data?.friendly_error_message || fallback;
        }
        setErrorMessage(message);
    });
};
export default confirmBet;
